// Sprite-sheet helpers. The art ships as a few packed sheets in public/img and
// we show one cell at a time with the CSS background-position trick, rather than
// slicing the images into 44 separate files. Every consumer gets an inline
// style; the element it lands on just needs to carry the cell's aspect ratio
// (square for avatars, 3:2 for locations) so the cell is never stretched.

import type { CSSProperties } from "react"
import type { KnowledgeCategory } from "../types"
import { abilities, events, factions, locations, units } from "./content"
import speakersData from "../data/speakers.json"

const AVATAR_SHEET = "/img/avatars.png"
const AVATAR_COLS = 3
const AVATAR_ROWS = 4
/** How many faces the chooser offers. The sheet is a 3×4 grid. */
export const AVATAR_COUNT = AVATAR_COLS * AVATAR_ROWS

// One sheet per 16 locations, in the exact order of locations.json (which is the
// order of the `locations` array). Sheet 1 is places 1–16, sheet 2 is 17–32.
const LOCATION_SHEETS = ["/img/locations_1.png", "/img/locations_2.png"]
const LOCATION_COLS = 4
const LOCATION_ROWS = 4
const LOCATIONS_PER_SHEET = LOCATION_COLS * LOCATION_ROWS

// One sheet of faction portraits, a 4×4 grid in the exact order of
// factions.json (which is the order of the `factions` array). The last cell is
// blank; the fifteenth faction is the last one drawn.
const FACTION_SHEET = "/img/factions.png"
const FACTION_COLS = 4
const FACTION_ROWS = 4

// One sheet of faction crests — heraldic shields, a 4×4 grid in the exact
// order of factions.json (same packing as FACTION_SHEET). Fifteen cells are
// drawn; the sixteenth is blank. Unlike the faction *portrait* (a leader's
// face), the crest is the realm's standing banner — used where the institution,
// not the person, is what a badge should read as: army headers, standing
// chips, and who-holds-what markers on the map.
const CREST_SHEET = "/img/heraldic_crests.png"
const CREST_COLS = 4
const CREST_ROWS = 4

// One sheet of ability icons, a 4×4 grid in the exact order of abilities.json
// (which is the order of the `abilities` array). Cells past the thirteenth are
// blank; each new ability lights up the next cell with no per-ability wiring.
const ABILITY_SHEET = "/img/abilities.png"
const ABILITY_COLS = 4
const ABILITY_ROWS = 4

// One sheet per 16 units, in the exact order of units.json (which is the order
// of the `units` array). Sheet 1 is units 1–16, sheet 2 is 17–32, sheet 3 is
// 33–48, and sheet 4 holds 49 onward. Cells are picked purely by the unit's
// position in the ordered list, so a new unit lights up the next cell with no
// per-unit wiring, and a not-yet-added sheet simply shows nothing.
const UNIT_SHEETS = [
  "/img/units_1.png",
  "/img/units_2.png",
  "/img/units_3.png",
  "/img/units_4.png",
]
const UNIT_COLS = 4
const UNIT_ROWS = 4
const UNITS_PER_SHEET = UNIT_COLS * UNIT_ROWS

// One sheet of event scenes, a 4×4 grid in the exact order of events.json (which
// is the order of the `events` array). Cells past the last event are blank; each
// new event lights up the next cell with no per-event wiring.
const EVENT_SHEET = "/img/events.png"
const EVENT_COLS = 4
const EVENT_ROWS = 4

/**
 * The background-position for cell (col,row) in a grid shown at
 * background-size: cols*100% rows*100%. With that sizing each axis is addressed
 * as a fraction of the *gaps* between cells, so column c of N is c/(N-1).
 */
function cellPosition(col: number, row: number, cols: number, rows: number): string {
  const x = cols > 1 ? (col / (cols - 1)) * 100 : 0
  const y = rows > 1 ? (row / (rows - 1)) * 100 : 0
  return `${x}% ${y}%`
}

/** The style that shows avatar `index` (0-based) from the avatar sheet. */
export function getAvatarStyle(index: number): CSSProperties {
  const safe = ((Math.trunc(index) % AVATAR_COUNT) + AVATAR_COUNT) % AVATAR_COUNT
  const col = safe % AVATAR_COLS
  const row = Math.floor(safe / AVATAR_COLS)
  return {
    backgroundImage: `url("${AVATAR_SHEET}")`,
    backgroundSize: `${AVATAR_COLS * 100}% ${AVATAR_ROWS * 100}%`,
    backgroundPosition: cellPosition(col, row, AVATAR_COLS, AVATAR_ROWS),
    backgroundRepeat: "no-repeat",
  }
}

/**
 * The style that shows a given location's portrait, or null if the id is
 * unknown (or falls on a sheet that has not been added yet). The cell is picked
 * purely by the location's position in the ordered list, so adding
 * locations_2.png makes places 17–32 light up with no further wiring.
 */
export function getLocationSpriteStyle(locationId: string): CSSProperties | null {
  const index = locations.findIndex((location) => location.id === locationId)
  if (index < 0) return null
  const sheet = LOCATION_SHEETS[Math.floor(index / LOCATIONS_PER_SHEET)]
  if (!sheet) return null
  const within = index % LOCATIONS_PER_SHEET
  const col = within % LOCATION_COLS
  const row = Math.floor(within / LOCATION_COLS)
  return {
    backgroundImage: `url("${sheet}")`,
    backgroundSize: `${LOCATION_COLS * 100}% ${LOCATION_ROWS * 100}%`,
    backgroundPosition: cellPosition(col, row, LOCATION_COLS, LOCATION_ROWS),
    backgroundRepeat: "no-repeat",
  }
}

/**
 * The style that shows a given faction's portrait, or null if the id is unknown
 * (or falls past the sheet's filled cells). Cells are picked purely by the
 * faction's position in the ordered list, so the sheet stays in step with
 * factions.json with no per-faction wiring.
 */
export function getFactionSpriteStyle(factionId: string): CSSProperties | null {
  const index = factions.findIndex((faction) => faction.id === factionId)
  if (index < 0 || index >= FACTION_COLS * FACTION_ROWS) return null
  const col = index % FACTION_COLS
  const row = Math.floor(index / FACTION_COLS)
  return {
    backgroundImage: `url("${FACTION_SHEET}")`,
    backgroundSize: `${FACTION_COLS * 100}% ${FACTION_ROWS * 100}%`,
    backgroundPosition: cellPosition(col, row, FACTION_COLS, FACTION_ROWS),
    backgroundRepeat: "no-repeat",
  }
}

/**
 * The style that shows a given faction's heraldic crest, or null if the id is
 * unknown (or falls past the sheet's filled cells). Cells are picked purely by
 * the faction's position in the ordered list, exactly like the portrait sheet,
 * so the crests stay in step with factions.json with no per-faction wiring.
 */
export function getFactionCrestStyle(factionId: string): CSSProperties | null {
  const index = factions.findIndex((faction) => faction.id === factionId)
  if (index < 0 || index >= CREST_COLS * CREST_ROWS) return null
  const col = index % CREST_COLS
  const row = Math.floor(index / CREST_COLS)
  return {
    backgroundImage: `url("${CREST_SHEET}")`,
    backgroundSize: `${CREST_COLS * 100}% ${CREST_ROWS * 100}%`,
    backgroundPosition: cellPosition(col, row, CREST_COLS, CREST_ROWS),
    backgroundRepeat: "no-repeat",
  }
}

/**
 * The style that shows a given ability's icon, or null if the id is unknown (or
 * falls past the sheet's filled cells). Cells are picked purely by the
 * ability's position in the ordered list, so the sheet stays in step with
 * abilities.json with no per-ability wiring.
 */
export function getAbilitySpriteStyle(abilityId: string): CSSProperties | null {
  const index = abilities.findIndex((ability) => ability.id === abilityId)
  if (index < 0 || index >= ABILITY_COLS * ABILITY_ROWS) return null
  const col = index % ABILITY_COLS
  const row = Math.floor(index / ABILITY_COLS)
  return {
    backgroundImage: `url("${ABILITY_SHEET}")`,
    backgroundSize: `${ABILITY_COLS * 100}% ${ABILITY_ROWS * 100}%`,
    backgroundPosition: cellPosition(col, row, ABILITY_COLS, ABILITY_ROWS),
    backgroundRepeat: "no-repeat",
  }
}

/**
 * The style that shows a given event's scene, or null if the id is unknown (or
 * falls past the sheet's filled cells). Cells are picked purely by the event's
 * position in the ordered list, so the sheet stays in step with events.json with
 * no per-event wiring.
 */
export function getEventSpriteStyle(eventId: string): CSSProperties | null {
  const index = events.findIndex((event) => event.id === eventId)
  if (index < 0 || index >= EVENT_COLS * EVENT_ROWS) return null
  const col = index % EVENT_COLS
  const row = Math.floor(index / EVENT_COLS)
  return {
    backgroundImage: `url("${EVENT_SHEET}")`,
    backgroundSize: `${EVENT_COLS * 100}% ${EVENT_ROWS * 100}%`,
    backgroundPosition: cellPosition(col, row, EVENT_COLS, EVENT_ROWS),
    backgroundRepeat: "no-repeat",
  }
}

// One sheet of Journal knowledge-category emblems, a 4×4 grid. Like the combat
// icons below (and unlike the per-entry event/ability sheets) this one is keyed
// by *category*, not array position, because a single emblem stands in for the
// many entries that share a kind. The order here is the exact order the cells
// are drawn: the twelve categories of KnowledgeCategory, then blank slots.
const KNOWLEDGE_SHEET = "/img/knowledge_icons.png"
const KNOWLEDGE_COLS = 4
const KNOWLEDGE_ROWS = 4
const KNOWLEDGE_CATEGORIES: KnowledgeCategory[] = [
  "omen",
  "rumor",
  "dispatch",
  "route",
  "victory",
  "alliance",
  "lore",
  "rival",
  "loop",
  "deadline",
  "doctrine",
  "triumph",
]

/**
 * The style that shows the emblem for a knowledge category, or null if the
 * category has no cell on the sheet. Callers pair this with getKnowledgeCategory
 * and can ask unconditionally: an entry whose category is unknown just renders
 * no icon.
 */
export function getKnowledgeIconStyle(category: KnowledgeCategory | null): CSSProperties | null {
  if (category === null) return null
  const index = KNOWLEDGE_CATEGORIES.indexOf(category)
  if (index < 0) return null
  const col = index % KNOWLEDGE_COLS
  const row = Math.floor(index / KNOWLEDGE_COLS)
  return {
    backgroundImage: `url("${KNOWLEDGE_SHEET}")`,
    backgroundSize: `${KNOWLEDGE_COLS * 100}% ${KNOWLEDGE_ROWS * 100}%`,
    backgroundPosition: cellPosition(col, row, KNOWLEDGE_COLS, KNOWLEDGE_ROWS),
    backgroundRepeat: "no-repeat",
  }
}

// One small sheet of combat icons, a 4×4 grid. Unlike the other sheets this one
// is keyed by *name* rather than by array position, because the same symbol is
// reused wherever a damage type or tag appears (damage line, resistances,
// counters). The order below is the exact order the cells are drawn: the six
// damage types, then the tags that were given a symbol. Keys not listed here
// (infantry, ranged, hero, …) simply have no icon.
const ICON_SHEET = "/img/icons.png"
const ICON_COLS = 4
const ICON_ROWS = 4
const ICON_KEYS = [
  "physical",
  "piercing",
  "fire",
  "shadow",
  "holy",
  "arcane",
  "undead",
  "dragon",
  "giant",
  "demon",
  "beast",
  "fortification",
  "anchor",
]

/**
 * The style that shows the icon for a damage type or unit tag, or null if that
 * key has no icon on the sheet. Callers can ask unconditionally: a key without a
 * symbol (infantry, cavalry, …) just renders nothing.
 */
export function getIconStyle(key: string): CSSProperties | null {
  const index = ICON_KEYS.indexOf(key)
  if (index < 0) return null
  const col = index % ICON_COLS
  const row = Math.floor(index / ICON_COLS)
  return {
    backgroundImage: `url("${ICON_SHEET}")`,
    backgroundSize: `${ICON_COLS * 100}% ${ICON_ROWS * 100}%`,
    backgroundPosition: cellPosition(col, row, ICON_COLS, ICON_ROWS),
    backgroundRepeat: "no-repeat",
  }
}

// Dialogue speaker portraits, packed across speakers_*.png. Unlike the other
// sheets this one is keyed by the speaker *string* (an alias table in
// speakers.json), not by array position, because the same face is reused
// wherever a speaker appears and several realm/aggregate voices share one
// portrait. The JSON pins each name to a sheet (1-based) and a cell in a
// COLSxROWS grid; we normalise names so lookup is case- and space-insensitive.
const SPEAKER_SHEETS = speakersData.sheets
const SPEAKER_COLS = speakersData.grid.cols
const SPEAKER_ROWS = speakersData.grid.rows

const normaliseSpeaker = (name: string) => name.trim().toLowerCase()

/** name -> { sheet (1-based), cell (0-based) }, one entry per speaker and alias. */
const SPEAKER_CELLS = new Map<string, { sheet: number; cell: number }>()
for (const entry of speakersData.speakers) {
  const target = { sheet: entry.sheet, cell: entry.cell }
  SPEAKER_CELLS.set(normaliseSpeaker(entry.speaker), target)
  for (const alias of (entry as { aliases?: string[] }).aliases ?? []) {
    SPEAKER_CELLS.set(normaliseSpeaker(alias), target)
  }
}

/**
 * The `--screen-backdrop` value for a specific battle: the battle's own scene
 * (public/img/battle_<battleId>.png) laid over the generic screen_battle.png as
 * a fallback. Unlike the packed sprite sheets, battle scenes are full-bleed
 * illustrations, so — like bg_menu/bg_death/bg_victory — each is its own file.
 * Stacking the generic image beneath means a battle whose bespoke art has not
 * been drawn yet still shows the shared battlefield rather than bare parchment,
 * and one that has been drawn paints over it. Fed as an inline custom-property
 * override on .app-content, so the existing battle backdrop CSS renders it with
 * no per-battle wiring (see App.css, .app-content[data-screen="battle"]).
 */
export function getBattleBackdropValue(battleId: string): string {
  return `url("/img/battle_${battleId}.png"), url("/img/screen_battle.png")`
}

/**
 * The style that shows a dialogue speaker's portrait, or null if that speaker
 * has no cell mapped (in which case the dialogue just shows its name, as before).
 * Callers can ask unconditionally: an unmapped or ambient speaker renders no
 * portrait rather than a wrong one.
 */
export function getSpeakerStyle(speaker: string | null | undefined): CSSProperties | null {
  if (!speaker) return null
  const target = SPEAKER_CELLS.get(normaliseSpeaker(speaker))
  if (!target) return null
  const sheet = SPEAKER_SHEETS[target.sheet - 1]
  if (!sheet) return null
  const col = target.cell % SPEAKER_COLS
  const row = Math.floor(target.cell / SPEAKER_COLS)
  return {
    backgroundImage: `url("${sheet}")`,
    backgroundSize: `${SPEAKER_COLS * 100}% ${SPEAKER_ROWS * 100}%`,
    backgroundPosition: cellPosition(col, row, SPEAKER_COLS, SPEAKER_ROWS),
    backgroundRepeat: "no-repeat",
  }
}

/**
 * The style that shows a given unit's portrait, or null if the id is unknown
 * (or falls on a sheet that has not been added yet). The cell is picked purely
 * by the unit's position in the ordered list, so the sheets stay in step with
 * units.json with no per-unit wiring.
 */
export function getUnitSpriteStyle(unitId: string): CSSProperties | null {
  const index = units.findIndex((unit) => unit.id === unitId)
  if (index < 0) return null
  const sheet = UNIT_SHEETS[Math.floor(index / UNITS_PER_SHEET)]
  if (!sheet) return null
  const within = index % UNITS_PER_SHEET
  const col = within % UNIT_COLS
  const row = Math.floor(within / UNIT_COLS)
  return {
    backgroundImage: `url("${sheet}")`,
    backgroundSize: `${UNIT_COLS * 100}% ${UNIT_ROWS * 100}%`,
    backgroundPosition: cellPosition(col, row, UNIT_COLS, UNIT_ROWS),
    backgroundRepeat: "no-repeat",
  }
}
